// components/CustomButton.tsx
import React, { MouseEventHandler } from 'react';
import { Button, SxProps } from '@mui/material';
import { theme } from '../../configs/theme';

type ButtonType = 'primary' | 'secondary' | 'success' | 'error' | 'tertiary';

type CustomButtonProps = {
  buttonType?: ButtonType;
  sx?: SxProps;
  children: React.ReactNode;
  fullWidth?: boolean;
  isDisabled?: boolean;
  fullHeight?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  component?: 'label' | 'button';
  size?: 'small' | 'large';
  'data-testid'?: string;
};

const buttonThemes: Record<
  ButtonType,
  {
    bgColor: string;
    textColor: string;
    hoverBgColor: string;
  }
> = {
  primary: {
    bgColor: theme.palette.customColors.emerald,
    textColor: theme.palette.common.white,
    hoverBgColor: theme.palette.customColors.emeraldHover,
  },
  secondary: {
    bgColor: theme.palette.customColors.grannySmith,
    textColor: theme.palette.customColors.emerald,
    hoverBgColor: theme.palette.customColors.grannySmithHover,
  },
  tertiary: {
    bgColor: theme.palette.customColors.bdazzledBlue,
    textColor: theme.palette.customColors.cultured,
    hoverBgColor: theme.palette.customColors.bdazzledBlueHover,
  },
  success: {
    bgColor: theme.palette.success.main,
    textColor: theme.palette.success.contrastText,
    hoverBgColor: theme.palette.success.dark,
  },
  error: {
    bgColor: theme.palette.error.main,
    textColor: theme.palette.error.contrastText,
    hoverBgColor: theme.palette.error.light,
  },
};

const ThemeButton: React.FC<CustomButtonProps> = ({
  buttonType = 'primary',
  sx,
  children,
  isDisabled = false,
  fullWidth,
  fullHeight = false,
  onClick,
  component = 'button',
  size = 'large',
  'data-testid': dataTestId,
}) => {
  const { bgColor, textColor, hoverBgColor } = buttonThemes[buttonType];

  return (
    <Button
      sx={{
        maxWidth: `${fullWidth ? 'auto' : '128px'}`,
        minWidth: size === 'large' ? '96px' : '48px',
        maxHeight: `${fullHeight ? 'auto' : '48px'}`,
        minHeight: '48px',
        bgcolor: bgColor,
        color: textColor,
        '&:hover': {
          bgcolor: hoverBgColor,
        },
        ...sx,
      }}
      fullWidth={fullWidth}
      variant="contained"
      onClick={onClick as MouseEventHandler<any>}
      disabled={isDisabled}
      component={component}
      data-testid={dataTestId}
    >
      {children}
    </Button>
  );
};

export default ThemeButton;
